import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { fetchRatings, type Rating, removeRating } from "../lib/api";

const CATEGORY_COLORS: Record<string, string> = {
	energy:
		"shadow-[6px_6px_0px_0px_#0052FF] hover:shadow-[4px_4px_0px_0px_#0052FF]",
	soda: "shadow-[6px_6px_0px_0px_#ad4600] hover:shadow-[4px_4px_0px_0px_#ad4600]",
	sparkling:
		"shadow-[6px_6px_0px_0px_#c1f100] hover:shadow-[4px_4px_0px_0px_#c1f100]",
};

const CATEGORY_ICONS: Record<string, string> = {
	energy: "bolt",
	soda: "local_drink",
	sparkling: "bubble_chart",
};

const CATEGORY_BG: Record<string, string> = {
	energy: "bg-secondary-container",
	soda: "bg-tertiary-fixed",
	sparkling: "bg-secondary-fixed",
};

const CATEGORY_TEXT_CLASS: Record<string, string> = {
	energy: "text-primary-container",
	soda: "text-tertiary-container",
	sparkling: "text-on-surface",
};

const FILTERS = ["all", "energy", "soda", "sparkling"] as const;

export function MyRatingsPage() {
	const queryClient = useQueryClient();
	const [activeFilter, setActiveFilter] = useState<string>("all");
	const [search, setSearch] = useState("");
	const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ["ratings", activeFilter, search],
		queryFn: () =>
			fetchRatings({
				category: activeFilter !== "all" ? activeFilter : undefined,
				search: search || undefined,
				limit: 100,
			}),
	});

	const deleteMutation = useMutation({
		mutationFn: removeRating,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["ratings"] });
			queryClient.invalidateQueries({ queryKey: ["stats"] });
			setDeleteConfirm(null);
		},
	});

	return (
		<div className="px-4 md:px-0">
			<header className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center mb-6 md:mb-8">
				<h1 className="font-headline-lg text-[28px] md:text-[32px] leading-[1.2] tracking-[-0.02em] font-bold font-[Space_Grotesk] uppercase text-primary-container">
					MY RATINGS
				</h1>

				<div className="flex items-center gap-3 md:gap-6">
					<div className="relative flex-1 md:flex-none">
						<span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface">
							search
						</span>
						<input
							className="w-full md:w-64 pl-10 pr-4 py-2 bg-surface-container-lowest border-[3px] border-brand-black font-label-bold text-[13px] leading-none font-bold font-[Space_Grotesk] uppercase focus:outline-none focus:border-primary-container focus:shadow-[4px_4px_0px_0px_#c1f100] transition-all"
							placeholder="FIND A DRINK..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>

					<Link
						to="/ratings/new"
						className="hidden md:flex bg-secondary-container text-on-surface font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase px-6 py-3 border-[3px] border-brand-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-all items-center gap-2"
					>
						RATE NOW
						<span className="material-symbols-outlined">bolt</span>
					</Link>
				</div>
			</header>

			<div className="flex flex-wrap gap-2 md:gap-4 mb-6 md:mb-8">
				{FILTERS.map((f) => (
					<button
						key={f}
						onClick={() => setActiveFilter(f)}
						className={`font-label-bold text-[12px] md:text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase px-3 md:px-4 py-2 md:py-3 rounded-full border-[3px] border-brand-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all active:translate-x-1 active:translate-y-1 active:shadow-none ${
							activeFilter === f
								? "bg-primary-container text-brand-white"
								: "bg-surface-container-lowest text-on-surface hover:bg-secondary-fixed"
						}`}
					>
						{f === "all" ? "ALL DRINKS" : f.toUpperCase()}
					</button>
				))}
			</div>

			{isLoading ? (
				<div className="flex justify-center py-8">
					<div className="font-label-bold text-on-surface uppercase animate-pulse">
						Loading...
					</div>
				</div>
			) : !data || data.data.length === 0 ? (
				<div className="bg-brand-white border-4 border-brand-black p-8 md:p-12 text-center shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
					<span
						className="material-symbols-outlined text-[60px] md:text-[80px] text-outline mb-4 block"
						style={{ fontVariationSettings: "'FILL' 1" }}
					>
						search_off
					</span>
					<h2 className="font-headline-md text-[20px] md:text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] uppercase mb-2">
						No Matches Found
					</h2>
					<p className="font-body-md text-[16px] leading-[1.5] text-outline">
						Try a different search or filter.
					</p>
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
					{data.data.map((rating) => (
						<RatingCard
							key={rating.id}
							rating={rating}
							onDeleteRequest={() => setDeleteConfirm(rating.id)}
						/>
					))}
				</div>
			)}

			{deleteConfirm && (
				<div className="fixed inset-0 bg-brand-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-brand-white border-4 border-brand-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] p-8 max-w-sm w-full">
						<h3 className="font-headline-md text-[24px] leading-[1.2] font-bold font-[Space_Grotesk] uppercase mb-4">
							Delete Rating?
						</h3>
						<p className="font-body-md text-[16px] leading-[1.5] text-outline mb-6">
							This can't be undone. Your stats will update immediately.
						</p>
						<div className="flex gap-4">
							<button
								onClick={() =>
									deleteMutation.mutate(deleteConfirm, {
										onError: () => setDeleteConfirm(null),
									})
								}
								disabled={deleteMutation.isPending}
								className="flex-1 bg-error text-on-error border-4 border-brand-black py-3 font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none transition-all"
							>
								{deleteMutation.isPending ? "Deleting..." : "Delete"}
							</button>
							<button
								onClick={() => setDeleteConfirm(null)}
								className="flex-1 border-[3px] border-brand-black py-3 font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

function RatingCard({
	rating,
	onDeleteRequest,
}: {
	rating: Rating;
	onDeleteRequest: () => void;
}) {
	return (
		<article
			className={`bg-surface-container-lowest border-[3px] border-brand-black p-4 flex flex-col gap-4 relative overflow-hidden group hover:-translate-y-1 hover:translate-x-1 transition-all cursor-pointer ${CATEGORY_COLORS[rating.category]}`}
		>
			<div className="flex justify-between items-start">
				<span
					className={`px-2 py-0.5 border-[3px] border-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase text-xs inline-block ${CATEGORY_BG[rating.category]} ${
						rating.category === "soda" ? "rotate-[3deg]" : "rotate-[-2deg]"
					}`}
				>
					{rating.category.toUpperCase()}
				</span>
				<div className="flex items-center gap-2">
					<span
						className={`material-symbols-outlined text-[32px] ${
							rating.category === "energy"
								? "text-primary-container"
								: rating.category === "soda"
									? "text-tertiary-container"
									: "text-on-surface"
						}`}
					>
						{CATEGORY_ICONS[rating.category]}
					</span>
					<button
						onClick={(e) => {
							e.stopPropagation();
							onDeleteRequest();
						}}
						className="opacity-0 group-hover:opacity-100 transition-opacity"
						title="Delete rating"
					>
						<span className="material-symbols-outlined text-on-surface-variant hover:text-error">
							delete
						</span>
					</button>
				</div>
			</div>

			<div>
				<h2 className="font-headline-lg text-[32px] leading-[1.2] tracking-[-0.02em] font-bold font-[Space_Grotesk] uppercase mb-2">
					{rating.drink_name}
				</h2>
				{rating.tasting_notes && (
					<p className="font-body-md text-[16px] leading-[1.5] text-on-surface-variant">
						{rating.tasting_notes}
					</p>
				)}
				{rating.brand && (
					<p className="font-body-md text-[16px] leading-[1.5] text-outline mt-1">
						{rating.brand}
					</p>
				)}
			</div>

			<div className="mt-auto pt-4 border-t-2 border-on-surface flex justify-between items-center">
				<div className={`flex gap-0.5 ${CATEGORY_TEXT_CLASS[rating.category]}`}>
					{[1, 2, 3, 4, 5].map((star) => (
						<span
							key={star}
							className="material-symbols-outlined"
							style={{
								fontVariationSettings: `'FILL' ${star <= rating.flavor_score ? 1 : 0}`,
							}}
						>
							star
						</span>
					))}
				</div>
				<span className="font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] text-outline uppercase">
					{timeAgo(rating.created_at)}
				</span>
			</div>

			{rating.vibe_tags.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{rating.vibe_tags.map((tag) => (
						<span
							key={tag}
							className="px-2 py-0.5 border-2 border-brand-black bg-secondary-container text-brand-black font-label-bold text-[14px] leading-none font-bold font-[Space_Grotesk] uppercase text-xs"
						>
							{tag}
						</span>
					))}
				</div>
			)}
		</article>
	);
}

function timeAgo(dateStr: string): string {
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diffSec = Math.floor((now - then) / 1000);

	if (diffSec < 60) return "just now";
	const mins = Math.floor(diffSec / 60);
	if (mins < 60) return `${mins} min ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours} hr ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days} day${days > 1 ? "s" : ""} ago`;
	const weeks = Math.floor(days / 7);
	if (weeks < 4) return `${weeks} wk ago`;
	const months = Math.floor(days / 30);
	return `${months} mo ago`;
}
